import moment from 'moment'
import { useMemo, useState } from 'react'
import { XIcon } from '@phosphor-icons/react'
import { useQuery } from 'urql'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { unsnooze, useActionCenterMomentum } from './momentumStore'
import { graphql } from 'app/generated/gql'
import type { ActionCenterSnoozedNamesQuery } from 'app/generated/gql/graphql'
import { useUser, useWorkspace } from 'app/utils'

const SNOOZED_NAMES_QUERY = graphql(`
  query ActionCenterSnoozedNames($search: String, $pagination: PaginationInput) {
    contacts(search: $search, pagination: $pagination) {
      hits {
        id
        name
      }
    }
  }
`)

/**
 * "N snoozed" on a queue card opens the list of who's snoozed, until when, and
 * lets you wake them early.
 */
export function SnoozedPopover({ queueId }: { queueId: string }) {
  const user = useUser()
  const workspace = useWorkspace()
  const { snoozes } = useActionCenterMomentum(user?.id, workspace?.id)
  const queueSnoozes = snoozes.filter((s) => s.queueId === queueId)
  const [open, setOpen] = useState(false)
  const idsKey = queueSnoozes
    .map((s) => s.contactId)
    .sort()
    .join(`,`)
  const variables = useMemo(
    () => ({
      search: `id:(${idsKey
        .split(`,`)
        .map((id) => `"${id}"`)
        .join(` OR `)})`,
      pagination: { limit: 100 },
    }),
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
    [idsKey],
  )
  const [namesResult] = useQuery<ActionCenterSnoozedNamesQuery>({
    query: SNOOZED_NAMES_QUERY,
    variables,
    pause: !open || !idsKey,
  })
  const names = useMemo(() => {
    const hits = namesResult.data?.contacts.hits
    if (!hits) return undefined
    return new Map(hits.filter((hit) => hit.id).map((hit) => [hit.id, hit.name || hit.id]))
  }, [namesResult.data])

  if (!workspace?.id || queueSnoozes.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-body-xs text-subtle transition hover:text-default hover:underline">
          {queueSnoozes.length} snoozed
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 gap-0 p-0">
        <p className="border-b border-subtle px-3 py-2 text-body-xs uppercase tracking-wide text-subtle">Snoozed</p>
        <div className="max-h-64 overflow-y-auto py-1">
          {queueSnoozes.map((entry) => (
            <div key={entry.contactId} className="flex items-center justify-between gap-2 px-3 py-1.5">
              <div className="min-w-0">
                <p className="truncate text-body-sm text-strong">{names?.get(entry.contactId) ?? `…`}</p>
                <p className="text-body-xs text-subtle">until {moment(entry.until).format(`MMM D`)}</p>
              </div>
              <button
                type="button"
                onClick={() => unsnooze(workspace.id!, entry.contactId, entry.queueId)}
                title="Wake now"
                className="shrink-0 rounded p-1 text-minimal transition hover:bg-surface-muted hover:text-default"
              >
                <XIcon size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
